import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Palette, Check } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { useApp } from '../context/AppContext';
import { useTranslation } from '../hooks/useTranslation';

const defaultCustomTheme = {
  background: '#1a1a2e',
  surface: '#16213e',
  text: '#eaeaea',
  primary: '#e94560',
  gold: '#ffd700',
};

const presetThemes = [
  {
    id: 'cyberpunk',
    name: 'Cyberpunk',
    colors: {
      background: '#0a0a0f',
      surface: '#1a1a2e',
      text: '#00fff5',
      primary: '#ff0080',
      gold: '#ffdd00',
    }
  },
  {
    id: 'forest',
    name: 'Forest',
    colors: {
      background: '#1a2f1a',
      surface: '#243524',
      text: '#d4e6d4',
      primary: '#4a9f4a',
      gold: '#d4a017',
    }
  },
  {
    id: 'ocean',
    name: 'Ocean',
    colors: {
      background: '#0d1b2a',
      surface: '#1b263b',
      text: '#e0e1dd',
      primary: '#00b4d8',
      gold: '#ffd166',
    }
  },
  {
    id: 'vampire',
    name: 'Vampire',
    colors: {
      background: '#0d0208',
      surface: '#1a0510',
      text: '#f5f5f5',
      primary: '#8b0000',
      gold: '#c9a227',
    }
  },
  {
    id: 'sunset',
    name: 'Sunset',
    colors: {
      background: '#2d1b2d',
      surface: '#3d2a3d',
      text: '#fff5e6',
      primary: '#ff6b35',
      gold: '#ffa502',
    }
  },
  {
    id: 'arctic',
    name: 'Arctic',
    colors: {
      background: '#e8f4f8',
      surface: '#ffffff',
      text: '#1a365d',
      primary: '#3182ce',
      gold: '#d69e2e',
    }
  },
];

const colorLabels = {
  it: {
    background: 'Sfondo',
    surface: 'Carte',
    text: 'Testo',
    primary: 'Accento',
    gold: 'Monete',
  },
  en: {
    background: 'Background',
    surface: 'Cards',
    text: 'Text',
    primary: 'Accent',
    gold: 'Coins',
  }
};

// Derive other colors from the main 5
const deriveFullTheme = (colors) => ({
  ...colors,
  surfaceHover: adjustBrightness(colors.surface, 10),
  textMuted: adjustBrightness(colors.text, -30),
  primaryHover: adjustBrightness(colors.primary, -15),
  border: adjustBrightness(colors.surface, 20),
  success: '#10b981',
  danger: '#ef4444',
});

// Adjust color brightness
const adjustBrightness = (hex, percent) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, Math.max(0, (num >> 16) + amt));
  const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
  const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
};

export const ThemeCustomizer = ({ isOpen, onClose }) => {
  const { state, dispatch } = useApp();
  const { lang } = useTranslation();
  const [customColors, setCustomColors] = useState(
    state.customTheme ? {
      background: state.customTheme.background,
      surface: state.customTheme.surface,
      text: state.customTheme.text,
      primary: state.customTheme.primary,
      gold: state.customTheme.gold,
    } : defaultCustomTheme
  );
  const [selectedPreset, setSelectedPreset] = useState(null);
  
  const labels = colorLabels[lang] || colorLabels.en;
  
  useEffect(() => {
    if (isOpen) {
      applyPreview(customColors);
    }
  }, [customColors, isOpen]);
  
  const applyPreview = (colors) => {
    const fullTheme = deriveFullTheme(colors);
    const root = document.documentElement;
    Object.entries(fullTheme).forEach(([key, value]) => {
      const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      root.style.setProperty(`--${cssKey}`, value);
    });
  };
  
  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset.id);
    setCustomColors(preset.colors);
  };
  
  const handleColorChange = (key, value) => {
    setSelectedPreset(null);
    setCustomColors(prev => ({ ...prev, [key]: value }));
  };
  
  const handleSave = () => {
    const fullTheme = deriveFullTheme(customColors);
    dispatch({ type: 'SET_CUSTOM_THEME', payload: fullTheme });
    dispatch({ type: 'SET_THEME', payload: 'custom' });
    onClose();
  };
  
  const handleClose = () => {
    // Revert to saved theme
    if (state.theme !== 'custom') {
      document.documentElement.setAttribute('data-theme', state.theme);
      const root = document.documentElement;
      ['background', 'surface', 'surface-hover', 'text', 'text-muted', 'primary', 'primary-hover', 'border', 'success', 'danger', 'gold'].forEach(key => {
        root.style.removeProperty(`--${key}`);
      });
    } else if (state.customTheme) {
      applyPreview(state.customTheme);
    }
    onClose();
  };
  
  const colorKeys = ['background', 'surface', 'text', 'primary', 'gold'];
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--surface)] rounded-t-3xl p-5 max-w-md mx-auto"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Palette size={24} weight="fill" className="text-[var(--primary)]" />
                <h2 className="text-lg font-bold text-[var(--text)]">
                  {lang === 'it' ? 'Personalizza' : 'Customize'}
                </h2>
              </div>
              <button onClick={handleClose} className="p-2 rounded-full hover:bg-[var(--background)]">
                <X size={20} className="text-[var(--text-muted)]" />
              </button>
            </div>
            
            {/* Presets */}
            <div className="mb-5">
              <p className="text-xs text-[var(--text-muted)] mb-2">
                {lang === 'it' ? 'Preset' : 'Presets'}
              </p>
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                {presetThemes.map(preset => (
                  <button
                    key={preset.id}
                    onClick={() => handlePresetSelect(preset)}
                    className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${
                      selectedPreset === preset.id 
                        ? 'bg-[var(--primary)] text-white' 
                        : 'bg-[var(--background)] text-[var(--text)]'
                    }`}
                    data-testid={`preset-${preset.id}`}
                  >
                    <div 
                      className="w-5 h-5 rounded-full border-2 border-white/30"
                      style={{ background: `linear-gradient(135deg, ${preset.colors.primary}, ${preset.colors.background})` }}
                    />
                    <span className="text-sm font-medium">{preset.name}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Colors */}
            <div className="grid grid-cols-5 gap-3 mb-5">
              {colorKeys.map(key => (
                <div key={key} className="flex flex-col items-center gap-1">
                  <div 
                    className="w-12 h-12 rounded-2xl border-2 border-[var(--border)] cursor-pointer relative overflow-hidden shadow-md"
                    style={{ backgroundColor: customColors[key] }}
                  >
                    <input
                      type="color"
                      value={customColors[key]}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      data-testid={`color-${key}`}
                    />
                  </div>
                  <span className="text-[10px] text-[var(--text-muted)] text-center">
                    {labels[key]}
                  </span>
                </div>
              ))}
            </div>
            
            {/* Apply Button */}
            <Button
              onClick={handleSave}
              className="w-full h-12 rounded-2xl bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white font-semibold"
              data-testid="save-theme-btn"
            >
              <Check size={20} className="mr-2" />
              {lang === 'it' ? 'Applica Tema' : 'Apply Theme'}
            </Button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
